/**
 * BookingsList
 *
 * Displays bookings for a scheduling link with booker info,
 * time, status, and cancel action for the host.
 */

import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import type { BookingDetail } from '@/lib/api';
import type { TimeFormat } from '../types';

interface BookingsListProps {
  bookings: BookingDetail[];
  onCancelBooking: (bookingId: string) => Promise<void>;
  timeFormat?: TimeFormat;
}

function formatDateTime(iso: string, timeFormat: TimeFormat = '12h'): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: timeFormat === '12h',
  });
}

export function BookingsList({ bookings, onCancelBooking, timeFormat = '12h' }: BookingsListProps) {
  if (bookings.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-4">No bookings yet.</p>;
  }

  return (
    <div className="space-y-2">
      {bookings.map((booking) => (
        <div
          key={booking.id}
          className={`flex items-start gap-2 border rounded-md px-3 py-2 ${
            booking.status === 'cancelled' ? 'opacity-50' : ''
          }`}
        >
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{booking.bookerName}</div>
            <div className="text-xs text-muted-foreground truncate">{booking.bookerEmail}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {formatDateTime(booking.startAt, timeFormat)} -{' '}
              {formatDateTime(booking.endAt, timeFormat)}
            </div>
            {booking.notes && (
              <div className="text-xs text-muted-foreground mt-0.5 italic truncate">
                {booking.notes}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <span
              className={`text-xs px-1.5 py-0.5 rounded ${
                booking.status === 'confirmed'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                  : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
              }`}
            >
              {booking.status}
            </span>
            {booking.status === 'confirmed' && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive"
                onClick={() => onCancelBooking(booking.id)}
                title="Cancel booking"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
